import React, { useMemo, useState, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseScheduleDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    return startOfDay(
      new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      ),
    );
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return startOfDay(parsed);
}

function getScheduleDateValue(schedule) {
  return (
    schedule?.SpecificDate ||
    schedule?.specificDate ||
    schedule?.Date ||
    schedule?.date ||
    null
  );
}

function buildMonthCells(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const cells = [];

  for (let i = 0; i < first.getDay(); i += 1) cells.push(null);
  for (let day = 1; day <= last.getDate(); day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const ScheduleCalendar = ({
  schedules = [],
  selectedKey,
  getScheduleKey,
  onSelect,
}) => {
  const today = useMemo(() => startOfDay(new Date()), []);

  const schedulesByDate = useMemo(() => {
    const map = new Map();
    schedules.forEach((schedule, index) => {
      const date = parseScheduleDate(getScheduleDateValue(schedule));
      if (!date) return;
      const key = toDateKey(date);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ schedule, index, date });
    });
    return map;
  }, [schedules]);

  const availableDates = useMemo(() => {
    return Array.from(schedulesByDate.values())
      .map((items) => items[0].date)
      .sort((a, b) => a - b);
  }, [schedulesByDate]);

  const initialMonth = availableDates[0] || today;
  const [viewMonth, setViewMonth] = useState(
    () => new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1),
  );
  const [selectedDate, setSelectedDate] = useState(
    () => availableDates[0] || null,
  );

  useEffect(() => {
    if (!availableDates.length) {
      setSelectedDate(null);
      return;
    }
    setSelectedDate((prev) => {
      if (prev && schedulesByDate.has(toDateKey(prev))) return prev;
      return availableDates[0];
    });
    setViewMonth(
      new Date(
        availableDates[0].getFullYear(),
        availableDates[0].getMonth(),
        1,
      ),
    );
  }, [availableDates, schedulesByDate]);

  useEffect(() => {
    if (!selectedKey || !schedules.length) return;
    const match = schedules.find(
      (schedule, index) => getScheduleKey(schedule, index) === selectedKey,
    );
    if (!match) return;
    const date = parseScheduleDate(getScheduleDateValue(match));
    if (!date) return;
    setSelectedDate(date);
    setViewMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  }, [selectedKey, schedules, getScheduleKey]);

  const monthLabel = viewMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);

  const selectDate = (date) => {
    if (!date) return;
    const slots = schedulesByDate.get(toDateKey(date));
    if (!slots?.length) return;
    setSelectedDate(date);
    // Auto-pick first schedule for the day (time UI is hidden)
    const { schedule, index } = slots[0];
    onSelect(schedule, index);
  };

  const canGoPrev = useMemo(() => {
    if (!availableDates.length) return false;
    const first = availableDates[0];
    return (
      viewMonth.getFullYear() > first.getFullYear() ||
      (viewMonth.getFullYear() === first.getFullYear() &&
        viewMonth.getMonth() > first.getMonth())
    );
  }, [availableDates, viewMonth]);

  const canGoNext = useMemo(() => {
    if (!availableDates.length) return false;
    const last = availableDates[availableDates.length - 1];
    return (
      viewMonth.getFullYear() < last.getFullYear() ||
      (viewMonth.getFullYear() === last.getFullYear() &&
        viewMonth.getMonth() < last.getMonth())
    );
  }, [availableDates, viewMonth]);

  const shiftMonth = (delta) => {
    setViewMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + delta);
      return next;
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Month nav */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: "optima",
            fontWeight: 700,
            fontSize: "15px",
            color: "#2B2B2B",
          }}
        >
          {monthLabel}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            disabled={!canGoPrev}
            onClick={() => shiftMonth(-1)}
            aria-label="Previous month"
            sx={{
              width: 28,
              height: 28,
              color: "#2B2B2B",
              "&.Mui-disabled": { color: "#D0D0D0" },
            }}
          >
            <FaChevronLeft size={11} />
          </IconButton>
          <IconButton
            size="small"
            disabled={!canGoNext}
            onClick={() => shiftMonth(1)}
            aria-label="Next month"
            sx={{
              width: 28,
              height: 28,
              color: "#2B2B2B",
              "&.Mui-disabled": { color: "#D0D0D0" },
            }}
          >
            <FaChevronRight size={11} />
          </IconButton>
        </Box>
      </Box>

      {/* Weekdays */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          mb: 0.5,
        }}
      >
        {WEEKDAYS.map((day, i) => (
          <Typography
            key={`${day}-${i}`}
            sx={{
              textAlign: "center",
              fontSize: "11px",
              fontWeight: 500,
              color: "#9A9A9A",
              fontFamily: "outfit",
              lineHeight: "20px",
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      {/* Days */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          rowGap: "2px",
        }}
      >
        {cells.map((date, idx) => {
          if (!date) {
            return <Box key={`empty-${idx}`} sx={{ height: 36 }} />;
          }

          const key = toDateKey(date);
          const available = schedulesByDate.has(key);
          const selected = isSameDay(date, selectedDate);
          const isToday = isSameDay(date, today);

          return (
            <Box
              key={key}
              component="button"
              type="button"
              disabled={!available}
              onClick={() => available && selectDate(date)}
              aria-label={date.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
              })}
              aria-pressed={selected}
              sx={{
                appearance: "none",
                WebkitAppearance: "none",
                border: "none",
                outline: "none",
                background: "transparent",
                p: 0,
                m: 0,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: available ? "pointer" : "default",
                "&:focus": { outline: "none" },
                "&:focus-visible .day": {
                  boxShadow: "0 0 0 2px rgba(188, 23, 47, 0.35)",
                },
              }}
            >
              <Box
                className="day"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  fontFamily: "outfit",
                  fontSize: "13px",
                  fontWeight: selected ? 600 : 400,
                  color: selected
                    ? "#fff"
                    : available
                      ? "#2B2B2B"
                      : "#C8C8C8",
                  bgcolor: selected ? "#BC172F" : "transparent",
                  border:
                    !selected && isToday && available
                      ? "1px solid #BC172F"
                      : "1px solid transparent",
                  transition: "background-color 0.12s ease, color 0.12s ease",
                  "&:hover":
                    available && !selected
                      ? { bgcolor: "rgba(188, 23, 47, 0.08)" }
                      : undefined,
                  "&::after":
                    available && !selected
                      ? {
                          content: '""',
                          position: "absolute",
                          bottom: 3,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 3,
                          height: 3,
                          borderRadius: "50%",
                          bgcolor: "#BC172F",
                        }
                      : undefined,
                }}
              >
                {date.getDate()}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ScheduleCalendar;
